// Remembers the onboarding steps the user has been sent to, in order, so that after going back
// "Continue" walks through them again instead of jumping to the first incomplete step.
// Steps the user skipped because their data was already complete (e.g. a tax ID from a previous
// onboarding) are never recorded, so they stay skipped.
const TAB_ROUTE = 'Main';
// Steps can be rendered inline in a tab, where the nearest navigator is the tab navigator and its
// routes are the tabs. The history works on the main stack's routes, so walk up to that navigator.
export const getMainStackRoutes = (navigation) => {
    let current = navigation;
    while (current && !current.getState()?.routeNames?.includes(TAB_ROUTE)) {
        current = current.getParent();
    }
    return current?.getState()?.routes ?? [];
};
// Screens a resolver shows for the same step, chosen by data the step itself fills in (the postcode search
// before the user has an address, the address form after). History stores the step, so revisiting it on
// the other screen is not treated as a new path.
const STEP_SCREENS = {
    AddressPostCodeScreen: 'InvestAddress',
    AutosaveSelectFundingAccount: 'SelectFundingAccount',
    AutoinvestSelectFundingAccount: 'SelectFundingAccount',
};
const toStep = (screen) => STEP_SCREENS[screen] ?? screen;
const visitedSteps = {
    invest: [],
    pots: [],
};
// The step the user is continuing from is the top-most route in the stack that we sent them to.
// Sub-screens pushed by a step (e.g. a confirmation screen) are not in the history, so they resolve to their step.
const findCurrentStepIndex = (flow, routes) => {
    for (let i = routes.length - 1; i >= 0; i--) {
        const index = visitedSteps[flow].indexOf(toStep(routes[i].name));
        if (index !== -1) {
            return index;
        }
    }
    return -1;
};
export const getStepsToRevisit = (flow, routes = []) => {
    const index = findCurrentStepIndex(flow, routes);
    return new Set(index === -1 ? [] : visitedSteps[flow].slice(index + 1));
};
export const recordOnboardingStep = (flow, routes = [], screen) => {
    const step = toStep(screen);
    const index = findCurrentStepIndex(flow, routes);
    if (index !== -1) {
        // Keep the steps still ahead of the user so they are revisited too. If the step is not among them
        // (an answer changed the path), the old path no longer applies.
        const stepsAhead = visitedSteps[flow].slice(index + 1);
        const stepIndex = stepsAhead.indexOf(step);
        visitedSteps[flow] = [
            ...visitedSteps[flow].slice(0, index + 1),
            ...(stepIndex === -1 ? [step] : stepsAhead.slice(stepIndex)),
        ];
        return;
    }
    // Starting a new history. Keep the screen we are leaving (e.g. SelectPotType, pushed directly by an entry
    // button) so continuing from it again also revisits. The tab renders the resume step inline, so it is not a step.
    const currentRoute = routes[routes.length - 1]?.name;
    visitedSteps[flow] =
        currentRoute && currentRoute !== TAB_ROUTE
            ? [toStep(currentRoute), step]
            : [step];
};
// Called when a flow completes or is abandoned, so a later run of it does not revisit stale steps.
export const clearOnboardingHistory = (flow) => {
    visitedSteps[flow] = [];
};
export const resetOnboardingHistory = () => {
    clearOnboardingHistory('invest');
    clearOnboardingHistory('pots');
};
